import React, { useEffect, useState } from "react";
import Navbar2 from "../components/navbar/Navbar2";
import AOS from "aos";
import "aos/dist/aos.css";
import { socket, api } from "../api";
import { useNavigate } from "react-router-dom";
import Footer from "../components/navbar/Footer";
import {
  FaTelegramPlane,
  FaFacebookF,
  FaInstagram,
  FaWhatsapp,
  FaTiktok,
  FaUserEdit,
  FaLock,
  FaUniversity,
  FaSignOutAlt,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { parseJWT } from "../utils/jwtUtils";

export default function ProfilePage() {
  useEffect(() => {
    AOS.init({
      offset: 100,
      duration: 500,
      easing: "ease-in-sine",
      delay: 100,
    });
    AOS.refresh();
  }, []);

  const navigate = useNavigate();
  const [balance, setBalance] = useState(0);
  const [totalIncome, setTotalIncome] = useState(0);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  const token = localStorage.getItem("userInfo")
    ? JSON.parse(localStorage.getItem("userInfo"))
    : null;

  const userInfo = parseJWT(token?.token);

  /* ================= BALANCE SOCKET ================= */

  useEffect(() => {
    if (userInfo?.phone) {
      socket.emit("getBalance", { phone: userInfo.phone });

      socket.on("balanceResponse", (data) => {
        if (data.success) {
          setBalance(data.balance);
        }
      });
    }

    return () => {
      socket.off("balanceResponse");
    };
  }, [userInfo?.phone]);

  /* ================= REFERRAL FETCH ================= */

  useEffect(() => {
    const fetchReferrals = async () => {
      try {
        if (!userInfo?.referralCode) {
          setMessage("00.0");
          setLoading(false);
          return;
        }

        const { data } = await api.get(`/referrals/${userInfo.referralCode}`);

        if (data.success) {
          setTotalIncome(data.totalIncome || 0);
          setMessage("");
        } else {
          setMessage(data.message || "Unable to load referral data.");
        }
      } catch (err) {
        console.error("Failed to fetch referral history:", err);
        setMessage("Failed to load referral history.");
      } finally {
        setLoading(false);
      }
    };

    fetchReferrals();
  }, []);

  /* ================= HANDLERS ================= */

  const handleEditProfile = () => navigate("/profile-update");

  const handleLogout = (e) => {
    e.preventDefault();
    localStorage.removeItem("userInfo");
    window.location.href = "/login";
  };

  const handleShareReferral = (refCode) => {
    if (!refCode) {
      alert("Referral code not found!");
      return;
    }

    const signupUrl = `https://enatgames.com/signup?ref=${refCode}`;
    const message = `Register using my referral code and earn 2 ETB bonus: ${refCode}\nSign up here: ${signupUrl}`;

    return {
      Telegram: `https://t.me/share/url?url=${encodeURIComponent(
        signupUrl,
      )}&text=${encodeURIComponent(message)}`,
      WhatsApp: `https://api.whatsapp.com/send?text=${encodeURIComponent(
        message,
      )}`,
      Facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        signupUrl,
      )}`,
      TikTok: `https://www.tiktok.com/share?url=${encodeURIComponent(
        signupUrl,
      )}`,
      Instagram: `https://www.instagram.com/`,
    };
  };

  const { t: translate } = useTranslation(["ProfilePage"]);

  return (
    <>
      {/* Navbar */}
      <div className="fixed left-0 right-0 top-0 z-50">
        <Navbar2 />
      </div>

      <div className="min-h-screen bg-primary text-white flex flex-col items-center pt-36 md:pt-24 px-4 pb-10">
        {/* ===== PROFILE HEADER ===== */}
        <div className="w-full max-w-3xl text-center mb-8">
          <h1 className="text-3xl font-bold font-serif">
            {translate("Profile")}
          </h1>
          <div className="w-24 h-[2px] bg-yellow-400 mx-auto mt-3 rounded-full"></div>
        </div>

        {/* ===== PROFILE CARD ===== */}
        <div className="w-full max-w-3xl bg-[#134454] p-8 rounded-2xl shadow-xl border border-[#185163] flex flex-col items-center gap-4">
          <div className="w-28 h-28 rounded-full bg-yellow-500 flex items-center justify-center text-4xl font-bold text-black shadow-lg">
            {userInfo?.username?.charAt(0)?.toUpperCase()}
          </div>

          <h2 className="text-2xl font-semibold">{userInfo?.username}</h2>

          <p className="text-gray-300 text-lg">📞 {userInfo?.phone}</p>

          <p className="text-gray-400 text-base">
            🎟 {translate("Referral_Code")} : {userInfo?.referralCode}
          </p>
        </div>

        {/* ===== WALLET ===== */}
        <div className="w-full max-w-3xl mt-8 bg-[#134454] rounded-2xl p-6 shadow-lg border border-[#185163]">
          <h2 className="text-xl font-semibold border-b border-gray-500 pb-3 font-serif">
            💰 {translate("Wallet_Summary")}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div className="bg-primary p-6 rounded-xl text-center shadow-inner">
              <p className="text-gray-400 text-lg">
                {translate("Main_Balance")}
              </p>
              <p className="text-3xl font-bold text-green-400 mt-2">
                {balance.toFixed(2)} {translate("ETB")}
              </p>
            </div>

            <div className="bg-primary p-6 rounded-xl text-center shadow-inner">
              <p className="text-gray-400 text-lg">
                {translate("Bonus_Balance")}
              </p>

              {loading ? (
                <p className="text-gray-300 mt-2">Loading...</p>
              ) : message ? (
                <p className="text-red-400 mt-2">{message}</p>
              ) : (
                <p className="text-3xl font-bold text-green-400 mt-2">
                  {totalIncome.toFixed(1)} {translate("ETB")}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ===== ACCOUNT SETTINGS ===== */}
        <div className="w-full max-w-3xl mt-8 bg-[#134454] rounded-2xl p-6 shadow-lg border border-[#185163]">
          <h2 className="text-xl font-semibold border-b border-gray-500 pb-3 font-serif">
            ⚙️ {translate("Account_Settings")}
          </h2>

          <div className="space-y-4 mt-6">
            <button
              onClick={handleEditProfile}
              className="w-full flex items-center gap-3 py-4 px-4 bg-primary hover:bg-cyan-800 rounded-xl text-lg font-semibold transition"
            >
              <FaUserEdit /> {translate("Edit_Profile")}
            </button>

            <button
              onClick={handleEditProfile}
              className="w-full flex items-center gap-3 py-4 px-4 bg-primary hover:bg-cyan-800 rounded-xl text-lg font-semibold transition"
            >
              <FaLock /> {translate("Change_Password")}
            </button>

            <button
              onClick={handleEditProfile}
              className="w-full flex items-center gap-3 py-4 px-4 bg-primary hover:bg-cyan-800 rounded-xl text-lg font-semibold transition"
            >
              <FaUniversity /> {translate("Manage_Bank")}
            </button>

            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 py-4 px-4 bg-red-950 hover:bg-red-700 rounded-xl text-lg font-semibold transition"
            >
              <FaSignOutAlt /> {translate("Logout")}
            </button>
          </div>
        </div>

        {/* ===== SHARE SECTION ===== */}
        <div className="w-full max-w-3xl mt-8 bg-[#134454] rounded-2xl p-6 shadow-lg border border-[#185163]">
          <h2 className="text-sm font-semibold border-b border-gray-500 pb-3 font-serif">
            🔗 {translate("share")}
          </h2>

          <div className="flex justify-center gap-4 mt-6">
            <button
              onClick={() =>
                window.open(
                  handleShareReferral(userInfo?.referralCode).Telegram,
                  "_blank",
                )
              }
              className="p-3 bg-blue-500 hover:bg-blue-600 rounded-full  transition"
            >
              <FaTelegramPlane size={24} />
            </button>

            <button
              onClick={() =>
                window.open(
                  handleShareReferral(userInfo?.referralCode).WhatsApp,
                  "_blank",
                )
              }
              className="p-3 bg-green-500 hover:bg-green-600 rounded-full transition"
            >
              <FaWhatsapp size={24} />
            </button>

            <button
              onClick={() =>
                window.open(
                  handleShareReferral(userInfo?.referralCode).Facebook,
                  "_blank",
                )
              }
              className="p-3 bg-blue-800 hover:bg-blue-900 rounded-full transition"
            >
              <FaFacebookF size={24} />
            </button>

            <button
              onClick={() =>
                window.open(
                  handleShareReferral(userInfo?.referralCode).Instagram,
                  "_blank",
                )
              }
              className="p-3 bg-pink-500 hover:bg-pink-600 rounded-full transition"
            >
              <FaInstagram size={24} />
            </button>

            <button
              onClick={() =>
                window.open(
                  handleShareReferral(userInfo?.referralCode).TikTok,
                  "_blank",
                )
              }
              className="p-3 bg-black hover:bg-gray-800 rounded-full transition"
            >
              <FaTiktok size={24} />
            </button>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}
